import type { AgentBody, AnswerMode, ModelAlias, ResolvedAgent } from '@ralph/contracts'
import { AnswerModeSchema, ModelAliasSchema } from '@ralph/contracts'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { ChevronDown, ChevronRight, History, RotateCcw, Save, TriangleAlert, X } from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import { type AgentLayer, isStaleWrite } from '@/api/agents.ts'
import { inheritedFrom, ResolvePreview } from '@/components/agents/ResolvePreview.tsx'
import { Notice } from '@/components/ui/admin-chrome.tsx'
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Panel,
  Readout,
  Select,
  Spinner,
  Textarea
} from '@/components/ui/primitives.tsx'
import { agentKeys, useAgent, useAgentVersions, useResolvedAgent, useSaveAgentLayer } from '@/hooks/use-agents.ts'
import { useTools } from '@/hooks/use-api.ts'
import { useUsableKnowledgeBases } from '@/hooks/use-knowledge-bases.ts'
import { describeApiError, type Messages, useI18n, useT } from '@/i18n/index.ts'

/**
 * The agent studio's editing surface.
 *
 * One stored definition over the platform's defaults. An agent is the same for everyone who can
 * use it (PRD-26 D-23): the per-group layers that once let one group's members get a different
 * prompt or budget are gone, because "who can use it" is a separate decision, made in the open
 * with the audience picker, and a second, invisible one here is how two people asking the same
 * agent the same question got different answers nobody could explain. Every control keeps its
 * third state — blank, meaning the platform default — and that default is shown as the
 * placeholder rather than stated as a rule to remember.
 *
 * Nothing here is set in the display face. The serif opens a screen — "Agent studio", one
 * heading up — and this is the instrument underneath it: an agent's name in a literary face
 * above a grid of budget ceilings would say the wrong thing about which of the two matters.
 */

type LayerDraft = {
  body: AgentBody
  displayName: string
  description: string
  enabled: boolean
  changeNote: string
}

const draftFromLayer = (layer: AgentLayer): LayerDraft => ({
  body: layer.body,
  displayName: layer.displayName,
  description: layer.description,
  enabled: layer.enabled,
  changeNote: ''
})

/**
 * Structural comparison with sorted keys.
 *
 * An edit that is typed and then undone reorders the body's keys, and plain
 * `JSON.stringify` would report that as an unsaved change forever.
 */
const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      // Code points, not the reader's collation: these are identifier KEYS being put in a
      // canonical order, not names being shown to somebody. `builder-form.ts:64` sorts its
      // canonical form the same way and for the same reason — a locale-sensitive order would
      // make the same object serialise differently in two languages.
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

/** Drops cleared keys, and the group itself once nothing is left, so "inherit" round-trips as absence. */
const prune = <T extends object>(group: T): T | undefined => {
  const entries = Object.entries(group).filter(([, value]) => value !== undefined)
  return entries.length === 0 ? undefined : (Object.fromEntries(entries) as T)
}

const parseNumber = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

/**
 * A numeric ceiling, identified by the KEY of its label rather than by the label.
 *
 * These tables are module-scope and stay that way — they are the form's field order, not
 * state — so they cannot hold words: a catalogue read at import freezes the language for the
 * session and then renders it after every locale switch, with no type error, no runtime error
 * and no failing test (PRD-27 F-3, LOC-012). The key is typed against the catalogue, so a
 * field whose label nobody wrote is a compile error here.
 */
type BudgetKey = keyof Messages['admin']['studio']['editor']['budgetFields']
type DelegationKey = 'maxDepth' | 'maxParallel'

type NumberSpec<K extends string = string> = {
  key: K
  min?: number
  step?: number
  read: (body: AgentBody) => number | undefined
  write: (body: AgentBody, value: number | undefined) => AgentBody
  resolved: (agent: ResolvedAgent) => number
}

const BUDGET_FIELDS: NumberSpec<BudgetKey>[] = [
  {
    key: 'maxSteps',
    min: 1,
    read: (b) => b.budget?.maxSteps,
    write: (b, v) => ({ ...b, budget: prune({ ...b.budget, maxSteps: v }) }),
    resolved: (a) => a.budget.maxSteps
  },
  {
    key: 'maxModelCalls',
    min: 1,
    read: (b) => b.budget?.maxModelCalls,
    write: (b, v) => ({ ...b, budget: prune({ ...b.budget, maxModelCalls: v }) }),
    resolved: (a) => a.budget.maxModelCalls
  },
  {
    key: 'maxToolCalls',
    min: 1,
    read: (b) => b.budget?.maxToolCalls,
    write: (b, v) => ({ ...b, budget: prune({ ...b.budget, maxToolCalls: v }) }),
    resolved: (a) => a.budget.maxToolCalls
  },
  {
    key: 'maxDelegations',
    min: 0,
    read: (b) => b.budget?.maxDelegations,
    write: (b, v) => ({ ...b, budget: prune({ ...b.budget, maxDelegations: v }) }),
    resolved: (a) => a.budget.maxDelegations
  },
  {
    key: 'maxCostUsd',
    min: 0,
    step: 0.01,
    read: (b) => b.budget?.maxCostUsd,
    write: (b, v) => ({ ...b, budget: prune({ ...b.budget, maxCostUsd: v }) }),
    resolved: (a) => a.budget.maxCostUsd
  },
  {
    key: 'maxWallClockMs',
    min: 1,
    step: 1000,
    read: (b) => b.budget?.maxWallClockMs,
    write: (b, v) => ({ ...b, budget: prune({ ...b.budget, maxWallClockMs: v }) }),
    resolved: (a) => a.budget.maxWallClockMs
  }
]

/**
 * Retrieval tuning and approval settings have no controls here any more.
 *
 * They are RuntimePolicy values (PRD-18 AGM-100): stored, versioned and administered in one
 * place rather than per agent. Leaving the inputs behind would be worse than removing them —
 * they were writing to fields nothing read, which is the dead configuration this phase exists
 * to end. `ResolvePreview` still SHOWS the resolved numbers, attributed to the policy they
 * came from, so an operator can still answer "why did this run search eight passages".
 */
const DELEGATION_FIELDS: NumberSpec<DelegationKey>[] = [
  {
    key: 'maxDepth',
    min: 0,
    read: (b) => b.delegation?.maxDepth,
    write: (b, v) => ({ ...b, delegation: prune({ ...b.delegation, maxDepth: v }) }),
    resolved: (a) => a.delegation.maxDepth
  },
  {
    key: 'maxParallel',
    min: 1,
    read: (b) => b.delegation?.maxParallel,
    write: (b, v) => ({ ...b, delegation: prune({ ...b.delegation, maxParallel: v }) }),
    resolved: (a) => a.delegation.maxParallel
  }
]

const MEMORY_WRITE_MODES = ['off', 'propose', 'auto'] as const

/** `embedding` is a real alias but cannot answer anything, so the studio does not offer it. */
const CHAT_MODEL_ALIASES = ModelAliasSchema.options.filter((alias) => alias !== 'embedding')

export const AgentEditor = ({ agentId, canEdit }: { agentId: string; canEdit: boolean }) => {
  // Inside the component, so every count, timestamp and WORD below follows a locale switch
  // instead of freezing at the moment this module was imported (PRD-27 F-3, LOC-012).
  const fmt = useI18n()
  const studio = fmt.t.admin.studio
  const t = studio.editor
  const detail = useAgent(agentId)
  const tools = useTools(true)
  const sources = useUsableKnowledgeBases()
  const [drafts, setDrafts] = useState<Record<string, LayerDraft>>({})
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const layers = detail.data?.layers ?? []
  const signature = layers.map((layer) => `${layer.scopeId}@${layer.version}`).join(' ')
  const baseline = useResolvedAgent(agentId, signature, layers.length > 0)
  const defaultLayer = layers[0] ?? null

  const toolOptions = (tools.data?.items ?? []).map((tool) => ({
    value: tool.name,
    label: tool.displayName,
    note: t.toolNote(studio.riskLabel[tool.risk], tool.requiresApproval)
  }))
  const sourceOptions = (sources.data?.items ?? []).map((source) => ({
    value: source.id,
    label: source.name,
    note: t.documentCount(fmt, source.documentCount)
  }))

  if (detail.isLoading) {
    return <p className="px-6 py-6 text-[13px] text-fg-subtle">{t.loading}</p>
  }

  if (detail.isError) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-10">
        {/* The refusal as a sentence rather than as whatever the server happened to say in
            English. A key this build knows renders in the reader's language with its specifics
            intact; one it does not falls back to the server prose, which is the deploy-order
            guarantee rather than a defect (PRD-27 D-4, LOC-033). */}
        <EmptyState
          title={t.failed}
          body={describeApiError(fmt.t, detail.error)}
          action={
            <Button size="sm" onClick={() => void detail.refetch()}>
              {t.retry}
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-6xl px-5 py-5 sm:px-6">
        <header>
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <h1 className="font-sans text-[19px] font-semibold tracking-[-0.02em]">
              {defaultLayer?.displayName || agentId}
            </h1>
            <span className="readout text-fg-subtle">{agentId}</span>
          </div>
          {defaultLayer?.description && (
            <p className="mt-1.5 max-w-2xl text-[13.5px] leading-relaxed text-fg-muted">{defaultLayer.description}</p>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <Readout label={t.versionLabel} value={defaultLayer?.version ?? 0} />
            {!canEdit && <span className="readout text-fg-subtle">{t.readOnly}</span>}
          </div>
        </header>

        {/* The preview column stretches with the row so its panel has room to stick. */}
        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-3">
            {layers.length === 0 && <EmptyState title={t.noDefinition} body={t.noDefinitionBody} />}

            {layers.map((layer, index) => (
              <LayerCard
                key={layer.scopeId}
                agentId={agentId}
                layer={layer}
                index={index}
                layers={layers}
                draft={drafts[layer.scopeId] ?? draftFromLayer(layer)}
                onDraft={(draft) => setDrafts((current) => ({ ...current, [layer.scopeId]: draft }))}
                onDiscard={() =>
                  setDrafts((current) => {
                    const next = { ...current }
                    delete next[layer.scopeId]
                    return next
                  })
                }
                canEdit={canEdit}
                baseline={baseline.data?.agent ?? null}
                toolOptions={toolOptions}
                sourceOptions={sourceOptions}
                collapsed={collapsed[layer.scopeId] ?? index > 0}
                onToggle={() =>
                  setCollapsed((current) => ({
                    ...current,
                    [layer.scopeId]: !(current[layer.scopeId] ?? index > 0)
                  }))
                }
              />
            ))}
          </div>

          <div>{layers.length > 0 && <ResolvePreview agentId={agentId} layers={layers} signature={signature} />}</div>
        </div>
      </div>
    </div>
  )
}

type Option = { value: string; label: string; note?: string }

type LayerCardProps = {
  agentId: string
  layer: AgentLayer
  index: number
  layers: AgentLayer[]
  draft: LayerDraft
  onDraft: (draft: LayerDraft) => void
  onDiscard: () => void
  canEdit: boolean
  baseline: ResolvedAgent | null
  toolOptions: Option[]
  sourceOptions: Option[]
  collapsed: boolean
  onToggle: () => void
}

const LayerCard = ({
  agentId,
  layer,
  index,
  layers,
  draft,
  onDraft,
  onDiscard,
  canEdit,
  baseline,
  toolOptions,
  sourceOptions,
  collapsed,
  onToggle
}: LayerCardProps) => {
  // Its own read rather than a prop threaded down from the editor: a card is rendered per
  // layer and the hook is what keeps each one subscribed to a locale change (PRD-27 F-3).
  const fmt = useI18n()
  const { formatAgo, formatList } = fmt
  const studio = fmt.t.admin.studio
  const t = studio.editor
  const client = useQueryClient()
  const save = useSaveAgentLayer()
  const fieldId = useId()
  const [metaDraft, setMetaDraft] = useState<string | null>(null)
  const [metaError, setMetaError] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const versions = useAgentVersions(agentId, showHistory)

  const body = draft.body
  const disabled = !canEdit
  const dirty =
    stableJson({
      body: draft.body,
      displayName: draft.displayName,
      description: draft.description,
      enabled: draft.enabled
    }) !==
    stableJson({
      body: layer.body,
      displayName: layer.displayName,
      description: layer.description,
      enabled: layer.enabled
    })

  const update = (next: AgentBody) => onDraft({ ...draft, body: next })

  /**
   * What a blank field on THIS layer falls back to.
   *
   * Only two answers are knowable from what the client holds: a lower layer that sets the
   * field, or — when no layer sets it anywhere — the resolved value, which must then be the
   * platform default. Anything else would be a guess about the environment layer, which the
   * admin API does not return, so it says "platform default" without a number instead.
   */
  const inheritedLabel = <T,>(
    read: (candidate: AgentBody) => T | undefined,
    resolved: (agent: ResolvedAgent) => T,
    /**
     * How to word the inherited value. `String` is right for the numbers and the wire
     * identifiers (a model alias is data, D-17) and wrong for a boolean, which used to render
     * the literal `true` — a wire value standing in for a word nobody wrote.
     */
    format: (value: T) => string = String
  ) => {
    const lower = inheritedFrom(layers, read, index)
    if (lower) return format(lower.value)
    if (inheritedFrom(layers, read, layers.length) === null && baseline) return format(resolved(baseline))
    return t.platformDefault
  }

  /** The same question for a list field, where "unknown" and "empty" must not look alike. */
  const inheritedList = (
    read: (candidate: AgentBody) => string[] | undefined,
    resolved: (agent: ResolvedAgent) => string[]
  ): string[] | null => {
    const lower = inheritedFrom(layers, read, index)
    if (lower) return lower.value
    if (inheritedFrom(layers, read, layers.length) === null && baseline) return resolved(baseline)
    return null
  }

  const commit = () =>
    save.mutate(
      {
        agentId,
        ifVersion: layer.version,
        body: draft.body,
        // A blank name is omitted rather than sent: the API rejects an empty string.
        ...(draft.displayName.trim() === '' ? {} : { displayName: draft.displayName.trim() }),
        description: draft.description,
        enabled: draft.enabled,
        ...(draft.changeNote.trim() === '' ? {} : { changeNote: draft.changeNote.trim() })
      },
      {
        onSuccess: () => {
          setMetaDraft(null)
          setMetaError(null)
          onDiscard()
        }
      }
    )

  const revert = () => {
    setMetaDraft(null)
    setMetaError(null)
    save.reset()
    onDiscard()
  }

  const reload = () => {
    revert()
    void client.invalidateQueries({ queryKey: agentKeys.detail(agentId) })
  }

  const overrides = (Object.keys(body) as Array<keyof AgentBody>)
    .filter((key) => body[key] !== undefined)
    .map((key) => t.overrideFields[key])

  const builtinOptions = [
    ...toolOptions,
    ...(body.tools?.builtin ?? [])
      .filter((name) => !toolOptions.some((option) => option.value === name))
      .map((name) => ({ value: name, label: name, note: t.unknownTool }))
  ]

  return (
    <Panel className="overflow-hidden">
      <header className="flex flex-wrap items-start gap-x-2.5 gap-y-2 border-b border-rule px-4 py-3.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t.expand : t.collapse}
          className="mt-0.5 shrink-0 rounded-full p-1 text-fg-subtle transition-colors hover:bg-card-solid hover:text-fg"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>

        <div className="min-w-0 flex-1 basis-[14rem]">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <h2 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.definition}</h2>
            {!draft.enabled && <Badge>{t.disabledBadge}</Badge>}
            {dirty && <Badge tone="primary">{t.unsavedBadge}</Badge>}
            {save.isPending && (
              <span className="readout flex items-center gap-1.5 text-primary">
                <Spinner />
                {t.savingBadge}
              </span>
            )}
          </div>
          {/* One sentence from the catalogue rather than three fragments joined here: the
              list is built by the one `Intl.ListFormat` in the repository (LOC-016) and the
              sentence around it is a whole entry (LOC-013). */}
          <p className="readout mt-1 text-fg-subtle">
            {t.layerMeta(
              layer.version,
              overrides.length === 0 ? t.overridesNothing : t.overridesSets(formatList(overrides))
            )}
          </p>
        </div>

        <label className="flex shrink-0 items-center gap-1.5 text-[12.5px] text-fg-muted">
          <input
            type="checkbox"
            checked={draft.enabled}
            disabled={disabled}
            onChange={(e) => onDraft({ ...draft, enabled: e.target.checked })}
            className="h-3.5 w-3.5 accent-primary"
          />
          {t.enabledLabel}
        </label>
      </header>

      {!collapsed && (
        <div className="space-y-5 px-4 py-4">
          <Section title={t.sections.identity}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t.displayNameLabel} htmlFor={`${fieldId}-name`}>
                <Input
                  id={`${fieldId}-name`}
                  value={draft.displayName}
                  disabled={disabled}
                  onChange={(e) => onDraft({ ...draft, displayName: e.target.value })}
                />
              </Field>
              <Field label={t.descriptionLabel} htmlFor={`${fieldId}-description`}>
                <Input
                  id={`${fieldId}-description`}
                  value={draft.description}
                  disabled={disabled}
                  placeholder={t.descriptionPlaceholder}
                  onChange={(e) => onDraft({ ...draft, description: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section title={t.sections.instructions}>
            <div>
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor={`${fieldId}-prompt`} className="eyebrow">
                  {t.promptLabel}
                </label>
                <span className="readout text-fg-subtle">{t.charCount(fmt, body.systemPrompt?.length ?? 0)}</span>
              </div>
              <Textarea
                id={`${fieldId}-prompt`}
                rows={10}
                value={body.systemPrompt ?? ''}
                disabled={disabled}
                placeholder={t.promptPlaceholder}
                className="mt-1 font-mono text-[12.5px] leading-relaxed"
                onChange={(e) => update({ ...body, systemPrompt: e.target.value === '' ? undefined : e.target.value })}
              />
              {body.systemPrompt === undefined && <p className="readout mt-1.5 text-alert">{t.promptMissing}</p>}
            </div>

            <div>
              <label htmlFor={`${fieldId}-prefix`} className="eyebrow">
                {t.prefixLabel}
              </label>
              <Textarea
                id={`${fieldId}-prefix`}
                rows={3}
                value={body.systemPromptPrefix ?? ''}
                disabled={disabled}
                placeholder={t.prefixPlaceholder}
                className="mt-1 font-mono text-[12.5px] leading-relaxed"
                onChange={(e) =>
                  update({ ...body, systemPromptPrefix: e.target.value === '' ? undefined : e.target.value })
                }
              />
              <p className="readout mt-1.5 text-fg-subtle">{t.prefixHint}</p>
            </div>
          </Section>

          <Section title={t.sections.model}>
            <div className="grid gap-3 sm:grid-cols-2">
              <InheritSelect
                id={`${fieldId}-model`}
                label={t.modelAliasLabel}
                value={body.modelAlias}
                options={CHAT_MODEL_ALIASES}
                inherited={inheritedLabel(
                  (candidate) => candidate.modelAlias,
                  (agent) => agent.modelAlias
                )}
                disabled={disabled}
                onChange={(value) => update({ ...body, modelAlias: value as ModelAlias | undefined })}
              />
              <InheritSelect
                id={`${fieldId}-mode`}
                label={t.answerModeLabel}
                value={body.answerMode}
                options={AnswerModeSchema.options}
                inherited={inheritedLabel(
                  (candidate) => candidate.answerMode,
                  (agent) => agent.answerMode
                )}
                disabled={disabled}
                onChange={(value) => update({ ...body, answerMode: value as AnswerMode | undefined })}
              />
            </div>
          </Section>

          <Section title={t.sections.tools}>
            <ListField
              label={t.builtinToolsLabel}
              hint={t.builtinToolsHint}
              value={body.tools?.builtin}
              inherited={inheritedList(
                (candidate) => candidate.tools?.builtin,
                (agent) => agent.tools.builtin
              )}
              options={builtinOptions}
              disabled={disabled}
              onChange={(next) => update({ ...body, tools: prune({ ...body.tools, builtin: next }) })}
            />
            <ListField
              label={t.connectedToolsLabel}
              hint={t.connectedToolsHint}
              value={body.tools?.mcp}
              inherited={inheritedList(
                (candidate) => candidate.tools?.mcp,
                (agent) => agent.tools.mcp
              )}
              entryPlaceholder={t.connectedToolsPlaceholder}
              disabled={disabled}
              onChange={(next) => update({ ...body, tools: prune({ ...body.tools, mcp: next }) })}
            />
          </Section>

          <Section title={t.sections.retrieval}>
            <ListField
              label={t.sourcesLabel}
              hint={t.sourcesHint}
              value={body.retrieval?.scopeIds}
              inherited={inheritedList(
                (candidate) => candidate.retrieval?.scopeIds,
                (agent) => agent.retrieval.scopeIds
              )}
              options={sourceOptions}
              disabled={disabled}
              onChange={(next) => update({ ...body, retrieval: prune({ ...body.retrieval, scopeIds: next }) })}
            />
          </Section>

          <Section title={t.sections.memory}>
            <div className="grid gap-3 sm:grid-cols-2">
              {/* The options were the literal wire values `true` and `false`, rendered as
                  option text (PRD-27 F-8). They are answers to a question now, in words
                  somebody chose — which is also the only form that has a Portuguese. */}
              <InheritSelect
                id={`${fieldId}-memory-read`}
                label={t.memoryReadLabel}
                value={body.memory?.read === undefined ? undefined : String(body.memory.read)}
                options={['true', 'false']}
                optionLabels={{ true: studio.yes, false: studio.no }}
                inherited={inheritedLabel(
                  (candidate) => candidate.memory?.read,
                  (agent) => agent.memory.read,
                  (value) => (value ? studio.yes : studio.no)
                )}
                disabled={disabled}
                onChange={(value) =>
                  update({
                    ...body,
                    memory: prune({ ...body.memory, read: value === undefined ? undefined : value === 'true' })
                  })
                }
              />
              <InheritSelect
                id={`${fieldId}-memory-write`}
                label={t.memoryWriteLabel}
                value={body.memory?.write}
                options={MEMORY_WRITE_MODES}
                optionLabels={t.memoryWrite}
                inherited={inheritedLabel(
                  (candidate) => candidate.memory?.write,
                  (agent) => agent.memory.write,
                  (value) => t.memoryWrite[value]
                )}
                disabled={disabled}
                onChange={(value) =>
                  update({
                    ...body,
                    memory: prune({
                      ...body.memory,
                      write: value === undefined ? undefined : (value as (typeof MEMORY_WRITE_MODES)[number])
                    })
                  })
                }
              />
            </div>
          </Section>

          <Section title={t.sections.budget}>
            <div className="grid gap-3 sm:grid-cols-3">
              {BUDGET_FIELDS.map((spec) => (
                <NumberCell
                  key={spec.key}
                  spec={spec}
                  label={t.budgetFields[spec.key]}
                  id={`${fieldId}-${spec.key}`}
                  body={body}
                  disabled={disabled}
                  update={update}
                  inheritedLabel={inheritedLabel}
                />
              ))}
            </div>
          </Section>

          <Section title={t.sections.delegation}>
            <ListField
              label={t.delegationFields.remoteAgents}
              hint={t.delegationFields.remoteAgentsHint}
              value={body.delegation?.remoteAgents}
              inherited={inheritedList(
                (candidate) => candidate.delegation?.remoteAgents,
                (agent) => agent.delegation.remoteAgents
              )}
              entryPlaceholder={t.delegationFields.remoteAgentsPlaceholder}
              disabled={disabled}
              onChange={(next) => update({ ...body, delegation: prune({ ...body.delegation, remoteAgents: next }) })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              {DELEGATION_FIELDS.map((spec) => (
                <NumberCell
                  key={spec.key}
                  spec={spec}
                  label={t.delegationFields[spec.key]}
                  id={`${fieldId}-${spec.key}`}
                  body={body}
                  disabled={disabled}
                  update={update}
                  inheritedLabel={inheritedLabel}
                />
              ))}
            </div>
          </Section>

          <Section title={t.sections.metadata}>
            <div>
              <label htmlFor={`${fieldId}-meta`} className="eyebrow">
                {t.metadataLabel}
              </label>
              <Textarea
                id={`${fieldId}-meta`}
                rows={3}
                disabled={disabled}
                className="mt-1 font-mono text-[12.5px]"
                placeholder={t.metadataPlaceholder}
                value={metaDraft ?? (body.meta ? JSON.stringify(body.meta, null, 2) : '')}
                onChange={(e) => {
                  const text = e.target.value
                  setMetaDraft(text)
                  if (text.trim() === '') {
                    setMetaError(null)
                    update({ ...body, meta: undefined })
                    return
                  }
                  try {
                    const parsed: unknown = JSON.parse(text)
                    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                      setMetaError(t.metadataNotObject)
                      return
                    }
                    setMetaError(null)
                    update({ ...body, meta: parsed as Record<string, unknown> })
                  } catch {
                    setMetaError(t.metadataNotJson)
                  }
                }}
              />
              {metaError && <p className="readout mt-1.5 text-alert">{metaError}</p>}
            </div>
          </Section>

          <div className="border-t border-rule pt-3.5">
            <button
              type="button"
              onClick={() => setShowHistory((open) => !open)}
              className="flex items-center gap-1.5 text-[13px] text-fg-muted transition-colors hover:text-fg"
            >
              <History size={13} aria-hidden />
              {showHistory ? t.historyHide : t.historyShow}
            </button>
            {showHistory && (
              <div className="fade-in mt-2">
                {versions.isLoading && <p className="text-[13px] text-fg-subtle">{t.historyLoading}</p>}
                {versions.isError && (
                  <p className="text-[12.5px] text-alert">{describeApiError(fmt.t, versions.error)}</p>
                )}
                {versions.data?.items.length === 0 && <p className="text-[13px] text-fg-subtle">{t.historyEmpty}</p>}
                {versions.data?.items.map((version) => (
                  <div
                    key={version.version}
                    className="flex items-baseline gap-2 border-b border-rule py-2 last:border-0"
                  >
                    <span className="readout w-8 shrink-0">v{version.version}</span>
                    <span className="min-w-0 flex-1 text-[13px]">
                      {version.changeNote || <span className="text-fg-subtle">{t.historyNoNote}</span>}
                    </span>
                    <span className="readout shrink-0 text-fg-subtle">{formatAgo(version.createdAt)}</span>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setMetaDraft(null)
                          setMetaError(null)
                          onDraft({ ...draft, body: version.body })
                        }}
                      >
                        {t.historyLoad}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isStaleWrite(save.error) && (
        <div className="mx-4 mb-3.5">
          <Notice title={t.staleTitle} icon={<TriangleAlert size={13} aria-hidden />}>
            <p>{t.staleBody(layer.version)}</p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              <Button size="sm" onClick={reload}>
                {t.staleReload}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => save.reset()}>
                {t.staleKeep}
              </Button>
            </div>
          </Notice>
        </div>
      )}

      {save.isError && !isStaleWrite(save.error) && (
        <p className="px-4 pb-2.5 text-[12.5px] text-alert">{describeApiError(fmt.t, save.error)}</p>
      )}

      <footer className="flex flex-wrap items-center gap-2 border-t border-rule px-4 py-3">
        <Input
          value={draft.changeNote}
          disabled={disabled}
          onChange={(e) => onDraft({ ...draft, changeNote: e.target.value })}
          placeholder={t.changeNotePlaceholder}
          aria-label={t.changeNoteLabel}
          className="h-[30px] min-w-[12rem] flex-1 text-[13px]"
        />
        {save.isSuccess && !dirty && <span className="readout text-grounded">{t.savedNote}</span>}
        <Button size="sm" variant="ghost" icon={<RotateCcw size={12} />} onClick={revert} disabled={!dirty}>
          {t.revert}
        </Button>
        <Button
          size="sm"
          variant="primary"
          icon={<Save size={12} />}
          onClick={commit}
          disabled={disabled || !dirty || save.isPending || metaError !== null}
        >
          {t.save}
        </Button>
      </footer>
    </Panel>
  )
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section>
    <h3 className="eyebrow border-b border-rule pb-1.5">{title}</h3>
    <div className="mt-3 space-y-3.5">{children}</div>
  </section>
)

const Field = ({
  label,
  htmlFor,
  hint,
  children
}: {
  label: string
  htmlFor: string
  hint?: string
  children: ReactNode
}) => (
  <div>
    <label htmlFor={htmlFor} className="eyebrow mb-1 block">
      {label}
    </label>
    {children}
    {hint && <p className="readout mt-1.5 text-fg-subtle">{hint}</p>}
  </div>
)

const InheritSelect = ({
  id,
  label,
  value,
  options,
  optionLabels,
  inherited,
  disabled,
  onChange
}: {
  id: string
  label: string
  value: string | undefined
  /** The wire values, which are what is stored and are never translated (PRD-27 D-17). */
  options: readonly string[]
  /**
   * The words for those values, when they have any. A model alias and an answer mode are
   * identifiers and render as themselves; a memory mode and a boolean are not, and rendering
   * `true` as option text is how a wire value ends up standing in for a word (F-8, LOC-018).
   */
  optionLabels?: Record<string, string>
  inherited: string
  disabled: boolean
  onChange: (value: string | undefined) => void
}) => {
  const t = useT().admin.studio.editor

  return (
    <Field label={label} htmlFor={id}>
      <Select
        id={id}
        value={value ?? ''}
        disabled={disabled}
        className="readout disabled:opacity-45"
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      >
        {/* The inherited value lives in the empty option's own label, so "what happens if I
            leave this alone" is readable without opening the menu. */}
        <option value="">{t.inheritOption(inherited)}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {optionLabels?.[option] ?? option}
          </option>
        ))}
      </Select>
    </Field>
  )
}

const NumberCell = ({
  spec,
  label,
  id,
  body,
  disabled,
  update,
  inheritedLabel
}: {
  spec: NumberSpec
  /** Looked up from the catalogue by the caller, because the spec table holds a key. */
  label: string
  id: string
  body: AgentBody
  disabled: boolean
  update: (body: AgentBody) => void
  inheritedLabel: (read: (body: AgentBody) => number | undefined, resolved: (agent: ResolvedAgent) => number) => string
}) => {
  const value = spec.read(body)
  return (
    <Field label={label} htmlFor={id}>
      <Input
        id={id}
        type="number"
        min={spec.min}
        step={spec.step}
        value={value ?? ''}
        disabled={disabled}
        placeholder={inheritedLabel(spec.read, spec.resolved)}
        className="readout"
        onChange={(e) => update(spec.write(body, parseNumber(e.target.value)))}
      />
    </Field>
  )
}

/**
 * A grant list with an explicit inherited state.
 *
 * Lists cannot use "blank means inherit" the way a scalar can: an empty allow-list is a
 * real, meaningful value ("this agent gets nothing"), and the merge treats it as one. So
 * inheriting is a state of the control rather than a state of its contents.
 */
const ListField = ({
  label,
  hint,
  value,
  inherited,
  options,
  entryPlaceholder,
  disabled,
  onChange
}: {
  label: string
  hint?: string
  value: string[] | undefined
  /** What applies while inherited; null when the platform default is not knowable here. */
  inherited: string[] | null
  options?: Option[]
  entryPlaceholder?: string
  disabled: boolean
  onChange: (value: string[] | undefined) => void
}) => {
  const { formatList, t: messages } = useI18n()
  const t = messages.admin.studio.editor
  const [entry, setEntry] = useState('')

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="eyebrow">{label}</span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(value === undefined ? [...(inherited ?? [])] : undefined)}
          className={clsx(
            'readout rounded-full border border-rule px-2.5 py-[3px] text-fg-subtle',
            'transition-colors duration-200 hover:border-rule-strong hover:text-fg disabled:opacity-45'
          )}
        >
          {value === undefined ? t.listInherited : t.listOverriding}
        </button>
      </div>

      {value === undefined ? (
        <p className="readout mt-1.5 text-fg-subtle">
          {inherited === null
            ? t.listFromLower
            : inherited.length === 0
              ? t.listEmpty
              : // The one `Intl.ListFormat` in the repository (LOC-016), rather than a comma
                // this file chose for itself — Portuguese joins its last item with "e" and
                // has no serial comma.
                formatList(inherited)}
        </p>
      ) : (
        <div className="mt-1.5 space-y-2">
          {options && options.length > 0 && (
            <div className="grid gap-1.5 sm:grid-cols-2">
              {options.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-2 rounded-tile px-1.5 py-1 text-[13px] hover:bg-card-solid"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(option.value)}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange(
                        e.target.checked
                          ? [...value, option.value]
                          : value.filter((granted) => granted !== option.value)
                      )
                    }
                    className="mt-1 h-3.5 w-3.5 shrink-0 accent-primary"
                  />
                  <span className="min-w-0">
                    <span className="block truncate">{option.label}</span>
                    {option.note && <span className="readout text-fg-subtle">{option.note}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}

          {entryPlaceholder && (
            <>
              <div className="flex flex-wrap gap-1.5">
                {value.map((granted) => (
                  <span
                    key={granted}
                    className="readout inline-flex items-center gap-1 rounded-full border border-rule bg-card px-2.5 py-[3px]"
                  >
                    {granted}
                    <button
                      type="button"
                      disabled={disabled}
                      aria-label={t.listRemove(granted)}
                      onClick={() => onChange(value.filter((current) => current !== granted))}
                      className="-mr-0.5 rounded-full p-0.5 text-fg-subtle transition-colors hover:text-alert"
                    >
                      <X size={10} strokeWidth={2.6} />
                    </button>
                  </span>
                ))}
                {value.length === 0 && <span className="readout text-fg-subtle">{t.listEmpty}</span>}
              </div>
              <div className="flex gap-2">
                <Input
                  value={entry}
                  disabled={disabled}
                  placeholder={entryPlaceholder}
                  aria-label={t.listAddTo(label)}
                  className="h-[30px] text-[13px]"
                  onChange={(e) => setEntry(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    const next = entry.trim()
                    if (next === '' || value.includes(next)) return
                    onChange([...value, next])
                    setEntry('')
                  }}
                />
                <Button
                  size="sm"
                  disabled={disabled || entry.trim() === '' || value.includes(entry.trim())}
                  onClick={() => {
                    onChange([...value, entry.trim()])
                    setEntry('')
                  }}
                >
                  {t.listAdd}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {hint && <p className="readout mt-1.5 text-fg-subtle">{hint}</p>}
    </div>
  )
}
